import {
  calculatedTransportDurationMinutes,
  isPendingTransportSource,
} from "../service";
import type { ItineraryNode, ItinerarySegment, RouteType, TransportMode } from "../types";

export const routeTypeModes: Record<RouteType, readonly TransportMode[]> = {
  main_transport: ["rail", "air"],
  charter_bus: ["bus"],
  local_transfer: ["walk"],
};

export { ITINERARY_TRANSPORT_LABELS as transportModeLabels } from "../config";

/** 为不带时区的行程日期增加一天，避免浏览器本地时区影响跨午夜判断。 */
function nextCalendarDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + 1));
  return date.toISOString().slice(0, 10);
}

/** 同一行程日内到达时刻早于出发时刻时，按跨午夜落到次日。 */
export function arrivalCalendarDate(
  departureDayDate: string | null | undefined,
  arrivalDayDate: string | null | undefined,
  departureTime: string,
  arrivalTime: string,
): string | null {
  if (!arrivalDayDate) return null;
  if (
    departureDayDate === arrivalDayDate
    && departureTime
    && arrivalTime
    && arrivalTime < departureTime
  ) {
    return nextCalendarDate(arrivalDayDate);
  }
  return arrivalDayDate;
}

/** 从行程 datetime 中提取原生 time 输入需要的时分值。 */
export function compactRouteTime(value?: string | null): string {
  return value?.slice(11, 16) || value || "";
}

/** 按无时区行程日期时间平移分钟，避免浏览器时区改变日历时刻。 */
export function shiftRouteDateTime(value: string, minutes: number): string | null {
  const matched = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!matched || !Number.isFinite(minutes)) return null;
  const [, year, month, day, hour, minute, second = "0"] = matched;
  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ) + minutes * 60_000;
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString().slice(0, 19) : null;
}

/** 完整时刻存在时以时刻差作为唯一时长事实。 */
export function calculatedRouteDurationMinutes(
  departureAt?: string | null,
  arrivalAt?: string | null,
): number | null {
  return calculatedTransportDurationMinutes(departureAt, arrivalAt);
}

/** 在路线时刻变化后同步刷新派生时长。 */
export function withCalculatedRouteDuration(segment: ItinerarySegment): ItinerarySegment {
  const durationMinutes = calculatedRouteDurationMinutes(segment.departureAt, segment.arrivalAt);
  return durationMinutes === null ? segment : { ...segment, durationMinutes };
}

/** 大巴或步行路线仅有一侧时刻时，按正预计时长补齐另一侧并保留跨午夜日期。 */
export function withEstimatedBusRouteTimes(
  segment: ItinerarySegment,
  departureDayDate?: string | null,
  arrivalDayDate?: string | null,
): ItinerarySegment {
  const durationMinutes = segment.durationMinutes;
  if (
    !["bus", "walk"].includes(segment.transportMode)
    || !departureDayDate
    || departureDayDate !== arrivalDayDate
    || durationMinutes === null
    || durationMinutes === undefined
    || !Number.isFinite(durationMinutes)
    || durationMinutes <= 0
  ) {
    return withCalculatedRouteDuration(segment);
  }
  if (segment.departureAt && !segment.arrivalAt) {
    return withCalculatedRouteDuration({
      ...segment,
      arrivalAt: shiftRouteDateTime(segment.departureAt, durationMinutes),
    });
  }
  if (!segment.departureAt && segment.arrivalAt) {
    let arrivalAt = segment.arrivalAt;
    let departureAt = shiftRouteDateTime(arrivalAt, -durationMinutes);
    const inferredDepartureDate = departureAt?.slice(0, 10);
    if (inferredDepartureDate && inferredDepartureDate < departureDayDate) {
      const missingDays = Math.round((
        Date.parse(`${departureDayDate}T00:00:00Z`)
        - Date.parse(`${inferredDepartureDate}T00:00:00Z`)
      ) / 86_400_000);
      arrivalAt = shiftRouteDateTime(arrivalAt, missingDays * 1_440) ?? arrivalAt;
      departureAt = shiftRouteDateTime(arrivalAt, -durationMinutes);
    }
    if (departureAt?.slice(0, 10) === departureDayDate) {
      return withCalculatedRouteDuration({ ...segment, departureAt, arrivalAt });
    }
  }
  return withCalculatedRouteDuration(segment);
}

/** 切换交通方式时清理旧方案事实，端点和用户填写的说明仍保留在当前工作区。 */
export function switchRouteTransportMode(
  draft: ItinerarySegment,
  routeType: RouteType,
  transportMode: TransportMode,
): ItinerarySegment {
  return {
    ...draft,
    routeType,
    transportMode,
    transportSpuId: null,
    transportSkuId: null,
    busSpuId: null,
    busSkuId: null,
    routeCode: null,
    departureAt: null,
    arrivalAt: null,
    durationMinutes: null,
    distanceKm: null,
    sourcePayload: null,
  };
}

/** 打开编辑器时修正旧数据中路线类型与交通方式不匹配的组合。 */
export function initializeRouteDraft(segment: ItinerarySegment): ItinerarySegment {
  const next = structuredClone(segment);
  const selected = next.sourcePayload?.selected;
  if (selected) {
    next.routeCode = selected.routeCode ?? next.routeCode;
    next.departureAt = selected.departureAt ?? next.departureAt;
    next.arrivalAt = selected.arrivalAt ?? next.arrivalAt;
    next.durationMinutes = selected.durationMinutes ?? next.durationMinutes;
    next.distanceKm = selected.distanceKm ?? next.distanceKm;
  }
  if (!routeTypeModes[next.routeType].includes(next.transportMode)) {
    next.transportMode = routeTypeModes[next.routeType][0];
  }
  return withCalculatedRouteDuration(next);
}

/** 把人工保存动作投影为可持久化路线，清除默认步行或待确认来源。 */
export function prepareRouteDraftForSubmit(draft: ItinerarySegment): ItinerarySegment {
  const modes = routeTypeModes[draft.routeType];
  return {
    ...draft,
    transportMode: modes.includes(draft.transportMode) ? draft.transportMode : modes[0],
    sourceType: isPendingTransportSource(draft.sourceType) || draft.sourceType === "default_walk"
      ? "manual"
      : draft.sourceType,
  };
}

/** 校验铁路、航班端点类型。 */
export function routeConstraintWarning(
  segment: ItinerarySegment,
  fromNode: ItineraryNode | null,
  toNode: ItineraryNode | null,
): string {
  if (
    segment.transportMode === "rail"
    && (
      fromNode?.nodeType !== "railway_station"
      || !fromNode.pointSpuId?.trim()
      || toNode?.nodeType !== "railway_station"
      || !toNode.pointSpuId?.trim()
    )
  ) {
    return "铁路线路两端必须选择火车站点位";
  }
  if (
    segment.transportMode === "air"
    && (
      fromNode?.nodeType !== "airport"
      || !fromNode.pointSpuId?.trim()
      || toNode?.nodeType !== "airport"
      || !toNode.pointSpuId?.trim()
    )
  ) {
    return "航班线路两端必须选择机场点位";
  }
  return "";
}
